
import React from 'react';
import { Investment } from '@/types/investment';
import { formatCurrency } from './utils';

interface AmortizationScheduleProps {
  investment: Investment;
}

const AmortizationSchedule = ({ investment }: AmortizationScheduleProps) => {
  const generateAmortizationSchedule = () => {
    const balance = investment.currentBalance;
    const monthlyPayment = investment.monthlyPayment;
    const annualRate = investment.interestRate / 100;
    const monthlyRate = annualRate / 12;
    const remainingTerms = investment.remainingTerms;
    const balloonAmount = investment.balloonPaymentAmount || 0;
    
    const schedule = [];
    let currentBalance = balance;
    
    for (let month = 1; month <= remainingTerms; month++) {
      const interestPayment = currentBalance * monthlyRate;
      let principalPayment = monthlyPayment - interestPayment;
      let balloonPayment = 0;
      
      // If this is the last payment and there's a balloon payment
      if (month === remainingTerms && investment.hasBalloonPayment && balloonAmount > 0) {
        balloonPayment = balloonAmount;
        principalPayment = Math.max(0, currentBalance - balloonAmount);
      }
      
      const totalPayment = monthlyPayment + balloonPayment;
      const newBalance = currentBalance - principalPayment - balloonPayment;
      
      schedule.push({
        payment: month,
        principalPayment: Math.max(0, principalPayment),
        interestPayment: Math.max(0, interestPayment),
        balloonPayment: balloonPayment,
        totalPayment: totalPayment,
        remainingBalance: Math.max(0, newBalance)
      });
      
      currentBalance = newBalance;
      
      // Break if balance reaches zero
      if (currentBalance <= 0) break;
    }
    
    return schedule;
  };

  const schedule = generateAmortizationSchedule();
  const propertyAddress = investment.propertyAddress || `${investment.type} - ${investment.id}`;

  return (
    <div className="p-8 bg-white">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold mb-2">Amortization Schedule</h1>
        <h2 className="text-lg text-gray-600 mb-1">{propertyAddress}</h2>
        <div className="text-sm text-gray-500 space-y-1">
          <p>Current Balance: {formatCurrency(investment.currentBalance)}</p>
          <p>Monthly Payment: {formatCurrency(investment.monthlyPayment)}</p>
          <p>Interest Rate: {investment.interestRate}% Annual</p>
          <p>Remaining Terms: {investment.remainingTerms} months</p>
          {investment.hasBalloonPayment && investment.balloonPaymentAmount && (
            <p className="text-yellow-600 font-semibold">Balloon Payment: {formatCurrency(investment.balloonPaymentAmount)}</p>
          )}
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-3 py-2 text-left">Payment #</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Principal</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Interest</th>
              {investment.hasBalloonPayment && (
                <th className="border border-gray-300 px-3 py-2 text-right bg-yellow-200 text-yellow-800 font-semibold">Balloon</th>
              )}
              <th className="border border-gray-300 px-3 py-2 text-right">Total Payment</th>
              <th className="border border-gray-300 px-3 py-2 text-right">Remaining Balance</th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((payment) => (
              <tr key={payment.payment} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-3 py-2">{payment.payment}</td>
                <td className="border border-gray-300 px-3 py-2 text-right">
                  {formatCurrency(payment.principalPayment)}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-right">
                  {formatCurrency(payment.interestPayment)}
                </td>
                {investment.hasBalloonPayment && (
                  <td className={`border border-gray-300 px-3 py-2 text-right ${
                    payment.balloonPayment > 0 
                      ? 'bg-yellow-100 text-yellow-800 font-bold' 
                      : 'bg-yellow-50'
                  }`}>
                    {payment.balloonPayment > 0 ? formatCurrency(payment.balloonPayment) : '-'}
                  </td>
                )}
                <td className="border border-gray-300 px-3 py-2 text-right font-semibold">
                  {formatCurrency(payment.totalPayment)}
                </td>
                <td className="border border-gray-300 px-3 py-2 text-right">
                  {formatCurrency(payment.remainingBalance)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-6 text-center text-xs text-gray-500">
        <p>Generated on {new Date().toLocaleDateString()}</p>
        <p>This schedule is based on current balance and remaining terms</p>
        {investment.hasBalloonPayment && (
          <p className="text-yellow-600 font-semibold">* Balloon payment due with final payment</p>
        )}
      </div>
    </div>
  );
};

export default AmortizationSchedule;
